import { Alert,Button,Divider,Input,Space,Spin,Typography,theme } from "../ui";
import type { GetRef } from "../ui";
import React from "react";
import { LikeOutlined, DislikeOutlined, PlusOutlined, QuestionCircleOutlined, RightOutlined, SendOutlined } from "@ant-design/icons";
import { spacing } from "../layout/spacing";
import { drawerWidths } from "../components/drawerWidths";
import { FormDrawer } from "../components/FormDrawer";

const qeddyTypingIndicatorHeight = 18;
const qeddyTypingDotSize = 6;
const TYPING_DOT_INDICES = [0, 1, 2];

export type QeddyDrawerMessageKind =
  | "user"
  | "answer"
  | "clarifyingQuestion"
  | "suggestions"
  | "notAnswerable"
  | "conversationReset"
  | "conversationCompacted";

export type QeddyDrawerFeedbackReason = "notAnswered" | "unclear" | "outdated" | "stepsIncorrect";

export type QeddyDrawerMessage = {
  id: string;
  role: "user" | "assistant" | "system";
  kind: QeddyDrawerMessageKind;
  content: string;
  sources?: QeddyDrawerSource[];
  interactionId?: string | null;
  feedback?: "helpful" | "notHelpful" | null;
  feedbackReason?: QeddyDrawerFeedbackReason | null;
};

export type QeddyDrawerSource = {
  id: string;
  title: string;
  menuPath: string;
  routes?: string[];
};

export type QeddyDrawerSuggestion = {
  id: string;
  title: string;
  question: string;
};

export type QeddyDrawerLabels = {
  title: string;
  intro: string;
  inputPlaceholder: string;
  send: string;
  empty: string;
  thinking: string;
  error: string;
  newConversation: string;
  sources: string;
  openPage: string;
  helpful: string;
  notHelpful: string;
  feedbackThanks: string;
  feedbackReasons: Record<QeddyDrawerFeedbackReason, string>;
  suggestionsTitle: string;
  retry: string;
};

export type QeddyDrawerProps = {
  open: boolean;
  labels: QeddyDrawerLabels;
  messages: QeddyDrawerMessage[];
  suggestions?: QeddyDrawerSuggestion[];
  loading?: boolean;
  submitting?: boolean;
  error?: string | null;
  onAsk: (question: string) => Promise<void> | void;
  onNewConversation: () => void;
  onFeedback?: (
    messageId: string,
    interactionId: string,
    helpful: boolean,
    reason?: QeddyDrawerFeedbackReason
  ) => Promise<void> | void;
  onRetry?: () => void;
  onClose: () => void;
};

export function QeddyDrawer({
  open,
  labels,
  messages,
  suggestions = [],
  loading = false,
  submitting = false,
  error,
  onAsk,
  onNewConversation,
  onFeedback,
  onRetry,
  onClose
}: QeddyDrawerProps) {
  const [question, setQuestion] = React.useState("");
  const [negativeFeedbackMessageId, setNegativeFeedbackMessageId] = React.useState<string | null>(null);
  const viewportRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<GetRef<typeof Input.TextArea>>(null);
  const followLatestRef = React.useRef(true);
  const wasSubmittingRef = React.useRef(submitting);

  React.useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (messages.length === 0) followLatestRef.current = true;
    if (open && viewport && followLatestRef.current) viewport.scrollTop = viewport.scrollHeight;
  }, [open, messages, submitting, error]);

  React.useEffect(() => {
    if (open && wasSubmittingRef.current && !submitting) inputRef.current?.focus({ preventScroll: true });
    wasSubmittingRef.current = submitting;
  }, [open, submitting]);

  React.useEffect(() => {
    if (open && messages.length === 0 && !submitting) inputRef.current?.focus({ preventScroll: true });
  }, [open, messages.length, submitting]);

  const close = () => {
    setQuestion("");
    setNegativeFeedbackMessageId(null);
    onClose();
  };
  const dirty = question.trim().length > 0;

  const submit = React.useCallback(() => {
    const value = question.trim();
    if (!value || submitting) return;
    followLatestRef.current = true;
    setQuestion("");
    void onAsk(value);
  }, [onAsk, question, submitting]);

  const newConversation = () => {
    if (submitting) return;
    setQuestion("");
    setNegativeFeedbackMessageId(null);
    onNewConversation();
  };
  const children = (
    <div style={{ display: "flex", flexDirection: "column", gap: spacing.sectionGap, height: "100%", minHeight: 0 }}>
      {error ? (
        <Alert
          type="warning"
          showIcon
          title={error || labels.error}
          action={onRetry ? <Button size="small" onClick={onRetry}>{labels.retry}</Button> : undefined}
        />
      ) : null}

      <div ref={viewportRef} role="log" aria-label={labels.title} aria-live="polite"
        onScroll={(event) => {
          const viewport = event.currentTarget;
          followLatestRef.current = viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight <= spacing.sectionGap;
        }}
        style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", overscrollBehavior: "contain" }}>
        {loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: spacing.pageGap }}>
            <Spin />
          </div>
        ) : (
          <Space orientation="vertical" size={spacing.sectionGap} style={{ width: "100%" }}>
            {messages.length === 0 ? (
              <QeddyEmptyState labels={labels} suggestions={suggestions} onAsk={onAsk} />
            ) : null}
            {messages.map((message) => (
              <QeddyMessageBubble
                key={message.id}
                message={message}
                labels={labels}
                onAsk={onAsk}
                onOpenPage={onClose}
                showNegativeReasons={negativeFeedbackMessageId === message.id}
                onHelpful={() => {
                  if (!message.interactionId || !onFeedback) return;
                  void onFeedback(message.id, message.interactionId, true);
                }}
                onNotHelpful={() => setNegativeFeedbackMessageId(message.id)}
                onFeedbackReason={(reason) => {
                  if (!message.interactionId || !onFeedback) return;
                  setNegativeFeedbackMessageId(null);
                  void onFeedback(message.id, message.interactionId, false, reason);
                }}
              />
            ))}
            {submitting ? <QeddyTypingBubble label={labels.thinking} /> : null}
          </Space>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "flex-end", gap: spacing.tightGap, width: "100%", flexShrink: 0 }}>
        <Input.TextArea
          ref={inputRef}
          style={{ flex: 1, minWidth: 0 }}
          aria-label={labels.inputPlaceholder}
          autoSize={{ minRows: 1, maxRows: 4 }}
          value={question}
          maxLength={1000}
          placeholder={labels.inputPlaceholder}
          disabled={submitting}
          onChange={(event) => setQuestion(event.target.value)}
          onPressEnter={(event) => {
            if (!event.shiftKey && !event.nativeEvent.isComposing) {
              event.preventDefault();
              submit();
            }
          }}
        />
        <Button type="primary" shape="circle" icon={<SendOutlined />} aria-label={labels.send} title={labels.send}
          disabled={submitting || !question.trim()} onClick={submit} style={{ flexShrink: 0 }} />
      </div>
    </div>
  );
  return <FormDrawer mode="panel" dirty={dirty} title={labels.title} open={open} onCancel={close} width={drawerWidths.qeddy}
     onSave={newConversation} submitText={labels.newConversation} submitIcon={<PlusOutlined />} submitDisabled={submitting}
    autoFocusFirstField={false} drawerProps={{ rootClassName: "qeddy-standard-drawer" }}>
    {children}
  </FormDrawer>;
}

function QeddyEmptyState({
  labels,
  suggestions,
  onAsk
}: {
  labels: QeddyDrawerLabels;
  suggestions: QeddyDrawerSuggestion[];
  onAsk: (question: string) => Promise<void> | void;
}) {
  return (
    <Space orientation="vertical" size={spacing.elementGap} style={{ width: "100%" }}>
      <Typography.Paragraph style={{ marginBottom: 0 }}>{labels.intro}</Typography.Paragraph>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>{labels.empty}</Typography.Paragraph>
      {suggestions.length > 0 ? (
        <Space orientation="vertical" size={spacing.tightGap} style={{ width: "100%" }}>
          <Typography.Text type="secondary">{labels.suggestionsTitle}</Typography.Text>
          <Space wrap size={spacing.tightGap}>
            {suggestions.map((suggestion) => (
              <Button key={suggestion.id} size="small" onClick={() => onAsk(suggestion.question)}>
                {suggestion.title}
              </Button>
            ))}
          </Space>
        </Space>
      ) : null}
    </Space>
  );
}

function QeddyTypingBubble({ label }: { label: string }) {
  const { token } = theme.useToken();
  return (
    <div role="status" aria-label={label}
      style={{ display: "flex", alignItems: "center", gap: spacing.tightGap, paddingBlock: spacing.tightGap, color: token.colorTextSecondary }}>
      <style>
        {`
          @keyframes qeddy-typing-dot {
            0%, 80%, 100% { opacity: 0.35; transform: translateY(0); }
            40% { opacity: 1; transform: translateY(-3px); }
          }
          @media (prefers-reduced-motion: reduce) {
            .qeddy-typing-dot { animation: none !important; }
          }
        `}
      </style>
        <span
          aria-hidden="true"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: spacing.microGap,
            flexShrink: 0,
            height: qeddyTypingIndicatorHeight
          }}
        >
          {TYPING_DOT_INDICES.map((index) => (
            <span
              key={index}
              className="qeddy-typing-dot"
              style={{
                width: qeddyTypingDotSize,
                height: qeddyTypingDotSize,
                borderRadius: "50%",
                background: "currentColor",
                animation: "qeddy-typing-dot 1.1s ease-in-out infinite",
                animationDelay: `${index * 0.16}s`
              }}
            />
          ))}
        </span>
      <Typography.Text type="secondary">{label}</Typography.Text>
    </div>
  );
}

function QeddyMessageBubble({
  message,
  labels,
  showNegativeReasons,
  onAsk,
  onOpenPage,
  onHelpful,
  onNotHelpful,
  onFeedbackReason
}: {
  message: QeddyDrawerMessage;
  labels: QeddyDrawerLabels;
  showNegativeReasons: boolean;
  onAsk: (question: string) => Promise<void> | void;
  onOpenPage: () => void;
  onHelpful: () => void;
  onNotHelpful: () => void;
  onFeedbackReason: (reason: QeddyDrawerFeedbackReason) => void;
}) {
  const { token } = theme.useToken();
  if (message.kind === "conversationReset" || message.kind === "conversationCompacted") {
    return <Divider plain><Typography.Text type="secondary">{message.content}</Typography.Text></Divider>;
  }

  const showFeedback = message.kind === "answer" && Boolean(message.interactionId);
  const isUser = message.role === "user";

  return (
    <div style={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start" }}>
      <Space orientation="vertical" size={spacing.tightGap} style={{ maxWidth: isUser ? "88%" : "100%", width: isUser ? undefined : "100%", minWidth: 0 }}>
        <div
          style={{
            margin: 0,
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
            lineHeight: token.lineHeight,
            // Nur die eigene Frage steht in einer Blase; Antworten lesen sich als Fließtext.
            ...(isUser ? { padding: `${spacing.tightGap}px ${spacing.elementGap}px`, borderRadius: token.borderRadiusLG, background: token.colorPrimaryBg } : {})
          }}
        >
          {message.role === "assistant" ? renderAssistantContent(message.content) : message.content}
        </div>
        {message.sources && message.sources.length > 0 ? (
          <QeddySourceLinks message={message} labels={labels} onAsk={onAsk} onOpenPage={onOpenPage} />
        ) : null}

        {showFeedback ? (
          message.feedback ? (
            <Typography.Text type="secondary">{labels.feedbackThanks}</Typography.Text>
          ) : (
            <Space orientation="vertical" size={spacing.microGap} style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
              <Space wrap size={spacing.tightGap}>
                <Button type="text" size="small" style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }} icon={<LikeOutlined />} onClick={onHelpful}>{labels.helpful}</Button>
                <Button type="text" size="small" style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }} icon={<DislikeOutlined />} onClick={onNotHelpful}>{labels.notHelpful}</Button>
              </Space>
              {showNegativeReasons ? (
                <Space wrap size={spacing.tightGap}>
                  {(Object.keys(labels.feedbackReasons) as QeddyDrawerFeedbackReason[]).map((reason) => (
                    <Button key={reason} size="small" type="text" onClick={() => onFeedbackReason(reason)}>
                      {labels.feedbackReasons[reason]}
                    </Button>
                  ))}
                </Space>
              ) : null}
            </Space>
          )
        ) : null}
      </Space>
    </div>
  );
}

/** Erste aufrufbare Seite einer Hilfe; Platzhalterrouten und die Startseite sind kein sinnvolles Ziel. */
function pageRoute(source: QeddyDrawerSource) {
  return source.routes?.map((route) => route.trim()).find((route) => route.length > 1 && route.startsWith("/") && !route.includes("/:"));
}

/**
 * Seiten zur Antwort als ganze, anklickbare Zeilen. Ablaufhilfen ohne eigene Seite erscheinen nur bei
 * Vorschlägen, dann als Rückfrage an Qeddy statt als Link auf eine unpassende Seite.
 */
function QeddySourceLinks({ message, labels, onAsk, onOpenPage }: {
  message: QeddyDrawerMessage;
  labels: QeddyDrawerLabels;
  onAsk: (question: string) => Promise<void> | void;
  onOpenPage: () => void;
}) {
  const { token } = theme.useToken();
  const seenRoutes = new Set<string>();
  const pages = (message.sources ?? []).flatMap((source) => {
    const route = pageRoute(source);
    if (!route || seenRoutes.has(route)) return [];
    seenRoutes.add(route);
    return [{ source, route }];
  });
  const topics = message.kind === "suggestions" ? (message.sources ?? []).filter((source) => !pageRoute(source)) : [];
  if (pages.length === 0 && topics.length === 0) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: spacing.tightGap, width: "100%" }}>
      <style>
        {`
          .qeddy-page-link { transition: border-color .15s ease, background-color .15s ease; }
          .qeddy-page-link:hover, .qeddy-page-link:focus-visible { border-color: ${token.colorPrimary}; background: ${token.colorPrimaryBg}; }
        `}
      </style>
      {pages.length > 0 && labels.sources ? <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>{labels.sources}</Typography.Text> : null}
      {pages.map(({ source, route }) => {
        const menuPath = source.menuPath?.trim();
        return (
          <a key={source.id} className="qeddy-page-link" href={normalizeHelpHref(route)} onClick={onOpenPage}
            aria-label={`${labels.openPage}: ${source.title}`}
            style={{
              display: "flex", alignItems: "center", gap: spacing.elementGap, textDecoration: "none",
              padding: `${spacing.tightGap}px ${spacing.elementGap}px`, borderRadius: token.borderRadiusLG,
              border: `1px solid ${token.colorBorderSecondary}`, color: token.colorText, background: token.colorBgContainer
            }}>
            <span style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
              <span style={{ fontWeight: token.fontWeightStrong, overflowWrap: "anywhere" }}>{source.title}</span>
              {menuPath && menuPath !== source.title ? (
                <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary, overflowWrap: "anywhere" }}>{menuPath}</span>
              ) : null}
            </span>
            <RightOutlined aria-hidden="true" style={{ color: token.colorTextSecondary, flexShrink: 0 }} />
          </a>
        );
      })}
      {topics.length > 0 ? (
        <Space wrap size={spacing.tightGap}>
          {topics.map((source) => (
            <Button key={source.id} size="small" icon={<QuestionCircleOutlined />} onClick={() => void onAsk(source.title)}>
              {source.title}
            </Button>
          ))}
        </Space>
      ) : null}
    </div>
  );
}

function renderAssistantContent(content: string) {
  const lines = content.split(/\r?\n/);
  const blocks: React.ReactNode[] = [];
  let paragraph: string[] = [];
  let items: string[] = [];
  let ordered = false;
  let firstNumber = 1;
  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    blocks.push(<p key={blocks.length} style={{ margin: 0 }}>{renderInlineMarkdown(paragraph.join("\n"), blocks.length)}</p>);
    paragraph = [];
  };
  const flushList = () => {
    if (items.length === 0) return;
    const children = items.map((item, index) => <li key={index} style={{ marginTop: index === 0 ? 0 : spacing.microGap }}>{renderInlineMarkdown(item, index)}</li>);
    const style = { margin: 0, paddingInlineStart: spacing.sectionGap };
    blocks.push(ordered
      ? <ol key={blocks.length} start={firstNumber} style={style}>{children}</ol>
      : <ul key={blocks.length} style={style}>{children}</ul>);
    items = [];
  };
  for (const line of lines) {
    const listItem = /^\s*(?:(\d+)[.)]|([-*]))\s+(.+)$/.exec(line);
    if (listItem) {
      flushParagraph();
      const nextOrdered = Boolean(listItem[1]);
      if (items.length > 0 && ordered !== nextOrdered) flushList();
      if (items.length === 0) {
        ordered = nextOrdered;
        firstNumber = Number(listItem[1] ?? 1);
      }
      items.push(listItem[3]);
    } else if (line.trim().length === 0) {
      flushParagraph();
      flushList();
    } else if (items.length > 0 && /^\s+/.test(line)) {
      items[items.length - 1] += `\n${line.trim()}`;
    } else {
      flushList();
      paragraph.push(line);
    }
  }
  flushParagraph();
  flushList();
  return <div style={{ display: "flex", flexDirection: "column", gap: spacing.elementGap }}>{blocks}</div>;
}

function renderInlineMarkdown(line: string, lineIndex: number) {
  const nodes: React.ReactNode[] = [];
  const pattern = /(\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|`([^`]+)`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(line)) !== null) {
    if (match.index > lastIndex) nodes.push(line.slice(lastIndex, match.index));
    if (match[2] && match[3]) {
      nodes.push(<a key={`${lineIndex}-${match.index}`} href={normalizeHelpHref(match[3])}>{match[2]}</a>);
    } else if (match[4]) {
      nodes.push(<strong key={`${lineIndex}-${match.index}`}>{match[4]}</strong>);
    } else if (match[5]) {
      // Backticks markieren in den Hilfen sichtbare Bezeichnungen der Oberfläche, keinen Code.
      nodes.push(<strong key={`${lineIndex}-${match.index}`}>{match[5]}</strong>);
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < line.length) nodes.push(line.slice(lastIndex));
  return nodes.length > 0 ? nodes : line;
}

function normalizeHelpHref(href: string) {
  const trimmed = href.trim();
  if (trimmed.startsWith("#/")) return trimmed;
  if (trimmed.startsWith("/")) return `#${trimmed}`;
  return "#/";
}
